import React, { useCallback, useMemo } from 'react';

import { AppstoreOutlined, CloudServerOutlined, ClusterOutlined, TableOutlined } from '@ant-design/icons';

import type { LineageHierarchyType } from '@/shared/types/lineage';
import { FloatButtonGroup } from '@/shared/ui/FloatButtonGroup';
import type { FloatButtonItem } from '@/shared/ui/FloatButtonGroup';

type HierarchyFilterProps = {
  availableHierarchies: {
    hasService: boolean;
    hasDomain: boolean;
    hasColumns: boolean;
  };
  selectedHierarchies: Set<LineageHierarchyType>;
  onHierarchiesChange: (hierarchies: Set<LineageHierarchyType>) => void;
};

const hierarchyConfig: Array<{
  type: LineageHierarchyType;
  icon: React.ReactNode;
  label: string;
  key: keyof HierarchyFilterProps['availableHierarchies'];
}> = [
  { type: 'service', icon: <CloudServerOutlined />, label: '서비스', key: 'hasService' },
  { type: 'domain', icon: <AppstoreOutlined />, label: '도메인', key: 'hasDomain' },
  { type: 'columns', icon: <TableOutlined />, label: '열', key: 'hasColumns' },
];

export const HierarchyFilter: React.FC<HierarchyFilterProps> = ({
  availableHierarchies,
  selectedHierarchies,
  onHierarchiesChange,
}) => {
  const handleToggleHierarchy = useCallback(
    (type: LineageHierarchyType) => {
      const newSelected = new Set<LineageHierarchyType>();

      if (selectedHierarchies.has(type)) {
        newSelected.clear();
      } else {
        newSelected.add(type);
      }

      onHierarchiesChange(newSelected);
    },
    [selectedHierarchies, onHierarchiesChange]
  );

  const items: FloatButtonItem[] = useMemo(() => {
    const result: FloatButtonItem[] = [];

    hierarchyConfig.forEach((config, index) => {
      const isAvailable = availableHierarchies[config.key];

      if (index === 2) {
        result.push({
          key: 'divider-1',
          icon: <></>,
          description: '',
          isDivider: true,
        });
      }

      result.push({
        key: config.type,
        icon: config.icon,
        description: config.label,
        onClick: () => handleToggleHierarchy(config.type),
        type: selectedHierarchies.has(config.type) ? 'primary' : 'default',
        toggleable: true,
        disabled: !isAvailable,
      });
    });

    return result;
  }, [availableHierarchies, selectedHierarchies, handleToggleHierarchy]);

  const isActive = selectedHierarchies.size > 0;

  return (
    <FloatButtonGroup
      icon={<ClusterOutlined />}
      description="계층"
      items={items}
      shape="square"
      size="medium"
      type={isActive ? 'primary' : 'default'}
      placement="right"
      trigger="click"
      autoCloseOnSelect={false}
      style={{ position: 'absolute', left: 24, bottom: 24 }}
    />
  );
};
